import {
  getAttorneyFieldIds,
  getCompanyFieldIds,
} from 'app/pages/loanApplication/tabs/loanClosingCDS/EditDrawer/EditDrawer.companyConfig';
import { loanInfoConfigFields } from 'app/pages/loanApplication/tabs/propertyAndLoan/EditModal/Content/MainContent/LoanInfo/LoanInfo.config';
import { INVALID_DATE_INPUT_ATTRIBUTE } from 'app/shared/componentsRestyled/coreDesignSystem/form/DateFieldCDS/DateFieldCDS.constants';

import { AttendantCompanyTypeEnum, LoanType } from 'constants/loanApplication';

export const DRAWER_MESSAGES = {
  FORM_NOT_FOUND: 'Form not found.',
  NO_SECTIONS: 'This form has no sections yet.',
  UNSUPPORTED_FIELDS_NOTICE:
    'Some fields configured in this form are not yet editable in this drawer and will be added in upcoming releases:',
  INAPPLICABLE_FIELDS_NOTICE:
    'Some fields configured in this form do not apply to the selected closing company:',
  UNTITLED_SECTION: 'Untitled section',
  CUSTOM_FIELDS_GROUP_TITLE: 'Custom fields',
  SAVE_SUCCESS: 'Loan info updated.',
  INVALID_FIELDS_FOR_SAVE: 'Some fields are invalid. Fix the highlighted fields before saving.',
  INVALID_DATE: 'Enter a valid date.',
  UNSUPPORTED_FALLBACK_LABEL: '(unnamed field)',
  BORROWER_SELECTOR_LABEL: 'Borrower',
  BORROWER_SELECTOR_PLACEHOLDER: 'Select a borrower',
  BORROWER_SELECTOR_HELPER:
    'This form contains borrower fields. Select which borrower to edit before continuing.',
  BORROWER_REQUIRED_FOR_SAVE: 'Select a borrower before saving.',
  NO_BORROWERS_AVAILABLE: 'No borrowers available on this loan.',
  CONTACT_SELECTOR_LABEL: 'Contact',
  CONTACT_SELECTOR_PLACEHOLDER: 'Select a contact',
  CONTACT_SELECTOR_HELPER:
    'This form contains contact fields. Select which contact to edit before continuing.',
  CONTACT_REQUIRED_FOR_SAVE: 'Select a contact before saving.',
  NO_CONTACTS_AVAILABLE: 'No contacts available on this loan.',
  CLOSING_COMPANY_SELECTOR_LABEL: 'Closing company',
  CLOSING_COMPANY_SELECTOR_PLACEHOLDER: 'Select a closing company',
  CLOSING_COMPANY_SELECTOR_HELPER:
    'This form contains closing company fields. Select which company to edit before continuing.',
  CLOSING_COMPANY_REQUIRED_FOR_SAVE: 'Select a closing company before saving.',
  NO_CLOSING_COMPANIES_AVAILABLE: 'No closing companies are set on this loan.',
};

export const DRAWER_BUTTON_NAMES = {
  ACCEPT: 'Save',
  CANCEL: 'Cancel',
};

export const LOAN_FIELD_KEY_FORM_INDEX = 3;
export const LOAN_FIELD_KEY_NAME_INDEX = 4;
export const LOAN_FIELD_KEY_MIN_PARTS = 5;

export const POST_CLOSING_FORM_PATH = 'postClosing';

export const APPRAISAL_FORM_PATH = 'appraisal';

export const CLOSING_FORM_PATH = 'closing';

export const FUNDING_FORM_PATH = 'funding';

export const CLOSING_COMPANIES = [
  {
    prefix: 'title',
    label: 'Title',
    closingCompanyType: AttendantCompanyTypeEnum.TITLE,
    getFieldIds: getCompanyFieldIds,
  },
  {
    prefix: 'escrow',
    label: 'Escrow',
    closingCompanyType: AttendantCompanyTypeEnum.ESCROW,
    getFieldIds: getCompanyFieldIds,
  },
  {
    prefix: 'sellerAttorney',
    label: "Seller's Attorney",
    closingCompanyType: AttendantCompanyTypeEnum.SELLER_ATTORNEY,
    getFieldIds: getAttorneyFieldIds,
  },
  {
    prefix: 'buyerAttorney',
    label: "Buyer's Attorney",
    closingCompanyType: AttendantCompanyTypeEnum.BUYER_ATTORNEY,
    getFieldIds: getAttorneyFieldIds,
  },
];

export const CLOSING_COMPANY_SELECTOR_FIELD_NAME = 'attendantCompanyId';

export const CLOSING_COMPANY_FIELD_NAMES = new Set([
  'escrowNumber',
  'closerName',
  'closerContactPhone',
  'closerContactEmail',
  'closerLicenseNumber',
  'contactPerson',
  'contactPhone',
  'contactEmail',
]);

export const SAVE_COMPANY_KEY_BY_TYPE = {
  [AttendantCompanyTypeEnum.TITLE]: 'titleCompany',
  [AttendantCompanyTypeEnum.ESCROW]: 'escrowCompany',
  [AttendantCompanyTypeEnum.SELLER_ATTORNEY]: 'sellerAttorney',
  [AttendantCompanyTypeEnum.BUYER_ATTORNEY]: 'buyerAttorney',
  [AttendantCompanyTypeEnum.BROKER]: 'brokerCompany',
  [AttendantCompanyTypeEnum.DEALER]: 'dealerCompany',
  [AttendantCompanyTypeEnum.INVESTOR]: 'investorCompany',
};

export const DEFAULT_APPRAISAL_NUMBER_OF_UNITS = 1;

export const DEFAULT_COLUMN_COUNT = 2;

export const LOADING_SKELETON_SECTION_COUNT = 2;
export const LOADING_SKELETON_FIELDS_PER_SECTION = 6;
export const LOADING_SKELETON_LABEL_WIDTH_PERCENT = 50;
export const LOADING_SKELETON_SECTION_TITLE_WIDTH_PERCENT = 28;
export const LOADING_SKELETON_DESCRIPTION_WIDTH_PERCENT = 70;

export const LOADING_SKELETON_LABEL_HEIGHT_PX = 12;
export const LOADING_SKELETON_INPUT_HEIGHT_PX = 40;
export const LOADING_SKELETON_SECTION_TITLE_HEIGHT_PX = 20;
export const LOADING_SKELETON_DESCRIPTION_HEIGHT_PX = 14;

export const EMPTY_LOAN_AND_LOCK_LIVE_VALUES = {};

export const INVALID_FIELD_SELECTOR = '[aria-invalid="true"]';

export const DATE_PICKER_SELECTOR = '.date-picker';

export const INVALID_DATE_INPUT_SELECTOR = `${DATE_PICKER_SELECTOR} input[${INVALID_DATE_INPUT_ATTRIBUTE}]`;

export const LOAN_AND_LOCK_HOUSING_FINANCE_AGENCY_FIELD_NAME = 'housingFinanceAgencyId';

export const LOAN_AND_LOCK_LIVE_SELECT_FIELD_NAMES = [
  'loanPurpose',
  'refinancePurpose',
  'tangibleNetBenefitReason',
  'nationalHousingActSection',
];

export const ESCROW_LABELS = {
  MASTER: 'Escrow:',
  TAXES: 'Taxes:',
  FLOOD: 'Flood',
};

export const ESCROW_MASTER_PATH = loanInfoConfigFields.isEscrowed.nameInModel;

export const ESCROW_TAXES_PATH = loanInfoConfigFields.isTaxesEscrowed.nameInModel;

export const INSURANCE_ESCROWED_FIELD_NAME = loanInfoConfigFields.isInsuranceEscrowed.fieldId;

export const BASE_LOAN_AMOUNT_FIELD_NAME = 'baseLoanAmount';

export const DOWN_PAYMENT_FIELD_NAME = 'downPayment';

export const GOVERNMENT_REFINANCE_PURPOSE_FIELD_NAME = 'governmentRefinancePurpose';

export const IS_FIELD_DEFAULT_A_USER_EDIT = false;

export const GOVERNMENT_REFINANCE_DEFAULT_LOAN_TYPES = new Set([LoanType.FHA, LoanType.VA]);

export const PURPOSE_INDEPENDENT_PROPERTY_FLAGS = {
  isDeedRestricted: true,
  isMultiUnit: true,
  isLeasehold: true,
  isMixedUseVisible: true,
  isManufacturedHome: true,
  isCompletionDateVisible: true,
  isFha: true,
  hasAffordableLegalRestrictions: true,
};

export const EVERY_LOAN_PURPOSE_FLAGS = {
  isPurchase: true,
  isRefinance: true,
  isAdditionalLoan: true,
  isHelocPurposePurchase: false,
  isConstructionToPermanent: true,
};
